import React, { useState, useMemo, useEffect } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Typography,
  Box,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { DateTime } from "luxon";
import { getErrorMessage } from "utils/errorHelpers";
import { formatDateAsLocaleDateString } from "utils/formatters/DateFormatters";
import { ExtensionType, type ExtendSubscriptionTermRequestDto, type TermExtension } from "client/api-v2";
import { useAlerts } from "components/AlertProvider/AlertProvider";
import DatePickerInput from "components/DatePickerInput/DatePickerInput";
import { createEnumChangeHandler } from "../utils/enum-utils";
import { SubscriptionExtensionHistory } from "./SubscriptionExtensionHistory";

// Maximum number of days a subscription term can be extended (1 year)
const MAX_TERM_EXTENSION_DAYS = 365;

interface ExtendSubscriptionTermDialogProps {
  open: boolean;
  subscriptionId: string;
  subscriptionName: string;
  currentTermStartDate: string | null | undefined;
  currentTermEndDate: string | null | undefined;
  extensions: TermExtension[] | undefined;
  handleClose: () => void;
  submitExtension: (request: ExtendSubscriptionTermRequestDto) => Promise<void>;
}

export function ExtendSubscriptionTermDialog({
  open,
  subscriptionId,
  subscriptionName,
  currentTermStartDate,
  currentTermEndDate,
  extensions,
  handleClose,
  submitExtension,
}: ExtendSubscriptionTermDialogProps) {
  const { sendNewAlert } = useAlerts();

  const [extensionType, setExtensionType] = useState<ExtensionType>(ExtensionType.TimeBased);
  const [reason, setReason] = useState<string>("");
  const [daysToAdd, setDaysToAdd] = useState<number>(30);
  const [newTermEndDate, setNewTermEndDate] = useState<DateTime | undefined>(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>();

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setExtensionType(ExtensionType.TimeBased);
      setReason("");
      setDaysToAdd(30);
      setNewTermEndDate(undefined);
      setError(undefined);
    }
  }, [open]);

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return "N/A";
    return formatDateAsLocaleDateString(DateTime.fromISO(dateString, { zone: "utc" }), false);
  };

  const minDateForPicker = useMemo(() => {
    if (!currentTermEndDate) return undefined;
    return DateTime.fromISO(currentTermEndDate, { zone: "utc" }).plus({ days: 1 });
  }, [currentTermEndDate]);

  const maxDateForPicker = useMemo(() => {
    if (!currentTermEndDate) return undefined;
    return DateTime.fromISO(currentTermEndDate, { zone: "utc" }).plus({ days: MAX_TERM_EXTENSION_DAYS });
  }, [currentTermEndDate]);

  // Validation logic
  const isValid = useMemo(() => {
    if (!reason.trim() || reason.length > 500) return false;

    if (extensionType === ExtensionType.TimeBased) {
      return daysToAdd >= 1 && daysToAdd <= MAX_TERM_EXTENSION_DAYS;
    }

    // Date-based validation
    if (!newTermEndDate || !currentTermEndDate || !maxDateForPicker) return false;
    const currentEnd = DateTime.fromISO(currentTermEndDate, { zone: "utc" });
    return newTermEndDate > currentEnd && newTermEndDate <= maxDateForPicker;
  }, [extensionType, reason, daysToAdd, newTermEndDate, currentTermEndDate, maxDateForPicker]);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError(undefined);

    try {
      const request: ExtendSubscriptionTermRequestDto = {
        extensionType,
        reason: reason.trim(),
        daysToAdd: extensionType === ExtensionType.TimeBased ? daysToAdd : undefined,
        newTermEndDate:
          extensionType === ExtensionType.DateBased ? newTermEndDate?.toISODate() ?? undefined : undefined,
      };

      await submitExtension(request);
      handleClose();
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setExtensionType(ExtensionType.TimeBased);
    setReason("");
    setDaysToAdd(30);
    setNewTermEndDate(undefined);
    setError(undefined);
    handleClose();
  };

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="md" fullWidth>
      <DialogTitle>Extend Subscription Term</DialogTitle>
      <DialogContent>
        <Grid container spacing={3}>
          {/* Current Context Section */}
          <Grid size={12}>
            <Box sx={{ mb: 2, mt: 1 }}>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Subscription:</strong> {subscriptionName}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                }}
              >
                <strong>Current Term:</strong> {formatDate(currentTermStartDate)} - {formatDate(currentTermEndDate)}
              </Typography>
            </Box>
          </Grid>

          {/* Extension Type Selection */}
          <Grid size={12}>
            <FormControl component="fieldset" fullWidth>
              <FormLabel component="legend">Extension Type</FormLabel>
              {
                <RadioGroup value={extensionType} onChange={createEnumChangeHandler(ExtensionType, setExtensionType)}>
                  <FormControlLabel
                    value={ExtensionType.TimeBased}
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="body1">Add Days</Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "text.secondary",
                          }}
                        >
                          Extend by adding a specific number of days to the current end date
                        </Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value={ExtensionType.DateBased}
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="body1">Set End Date</Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "text.secondary",
                          }}
                        >
                          Set a specific new end date for the subscription
                        </Typography>
                      </Box>
                    }
                  />
                </RadioGroup>
              }
            </FormControl>
          </Grid>

          {/* Conditional Input Fields */}
          {extensionType === ExtensionType.TimeBased ? (
            <Grid size={12}>
              <TextField
                fullWidth
                type="number"
                label="Days to Add"
                value={daysToAdd}
                onChange={(e) => setDaysToAdd(parseInt(e.target.value) || 0)}
                helperText={`Enter number of days (1-${MAX_TERM_EXTENSION_DAYS})`}
                error={daysToAdd < 1 || daysToAdd > MAX_TERM_EXTENSION_DAYS}
                required
                slotProps={{
                  htmlInput: { min: 1, max: MAX_TERM_EXTENSION_DAYS },
                }}
              />
            </Grid>
          ) : (
            <Grid size={12}>
              <DatePickerInput
                label="New Term End Date"
                value={newTermEndDate}
                onChange={(date: DateTime | null) => setNewTermEndDate(date ?? undefined)}
                minDate={minDateForPicker}
                maxDate={maxDateForPicker}
                referenceDate={currentTermEndDate ? DateTime.fromISO(currentTermEndDate, { zone: "utc" }) : undefined}
                showLocalConversion={true}
                required
                isInvalid={
                  !!newTermEndDate &&
                  ((!!currentTermEndDate && newTermEndDate <= DateTime.fromISO(currentTermEndDate, { zone: "utc" })) ||
                    (!!maxDateForPicker && newTermEndDate > maxDateForPicker))
                }
                errorMessage={
                  newTermEndDate &&
                  currentTermEndDate &&
                  newTermEndDate <= DateTime.fromISO(currentTermEndDate, { zone: "utc" })
                    ? "New end date must be after current end date"
                    : newTermEndDate && maxDateForPicker && newTermEndDate > maxDateForPicker
                    ? "New end date cannot be more than 1 year from current term end date"
                    : undefined
                }
              />
            </Grid>
          )}

          {/* Reason Field */}
          <Grid size={12}>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Reason for Extension"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required
              error={reason.length > 500}
              helperText={`${reason.length}/500 characters`}
              placeholder="Please provide a reason for extending this subscription..."
              slotProps={{
                htmlInput: { maxLength: 500 },
              }}
            />
          </Grid>

          {/* Extension History */}
          {extensions && extensions.length > 0 && (
            <Grid size={12}>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">Extension History ({extensions.length})</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <SubscriptionExtensionHistory extensions={extensions} />
                </AccordionDetails>
              </Accordion>
            </Grid>
          )}

          {/* Error Display */}
          {error && (
            <Grid size={12}>
              <Box sx={{ p: 2, bgcolor: "error.light", borderRadius: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: "error.dark",
                  }}
                >
                  {error}
                </Typography>
              </Box>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" color="primary" disabled={!isValid || isSubmitting}>
          {isSubmitting ? "Extending..." : "Extend Subscription"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
